import { useCallback, useEffect, useRef, useState } from "react";

export type PaymentProvider = "stripe" | "xsolla";

const ENDPOINT = process.env.REACT_APP_STRIPE_ENDPOINT;

const readQueryOverride = (): PaymentProvider | null => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search)
    .get("provider")
    ?.toLowerCase();
  return raw === "stripe" || raw === "xsolla" ? raw : null;
};

const fetchGeoProvider = async (): Promise<PaymentProvider> => {
  try {
    // The answer is per-viewer (edge geo). A cached response once pinned one
    // viewer's country onto everyone and sent non-RU users to Xsolla, so
    // bypass the HTTP cache and bust any shared cache keyed on the URL.
    const res = await fetch(
      `${ENDPOINT}/payment/v1/provider?nc=${Date.now()}`,
      {
        method: "GET",
        cache: "no-store",
      },
    );
    if (!res.ok) return "stripe";
    const data = (await res.json()) as { provider?: string };
    return data.provider === "xsolla" ? "xsolla" : "stripe";
  } catch {
    return "stripe";
  }
};

let cached: PaymentProvider | null = null;
let inflight: Promise<PaymentProvider> | null = null;

const resolveOnce = async (): Promise<PaymentProvider> => {
  const override = readQueryOverride();
  if (override) return override;
  if (cached) return cached;
  if (!inflight) {
    inflight = fetchGeoProvider().then((p) => {
      cached = p;
      inflight = null;
      return p;
    });
  }
  return inflight;
};

export const useResolvedPaymentProvider = () => {
  const [provider, setProvider] = useState<PaymentProvider | null>(
    () => readQueryOverride() ?? cached,
  );
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const resolve = useCallback(async (): Promise<PaymentProvider> => {
    const result = await resolveOnce();
    if (mounted.current) setProvider(result);
    return result;
  }, []);

  return { provider, resolve };
};
